import React, { useEffect, useState } from "react";
import "./Dashboard.css";
import { 
  FaMoneyBillWave, 
  FaWallet, 
  FaUsers, 
  FaTruck, 
  FaChartLine, 
  FaRegCalendarAlt, 
  FaHistory,
  FaFileInvoiceDollar
} from "react-icons/fa";
import ChartCard from "./charts/ChartCard";
import SalesByRegionChart from "./charts/SalesByRegionChart";
import RevenueTrendChart from "./charts/RevenueTrendChart";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from "recharts";

const CustomChartTooltip = ({ active, payload, label, unit = "" }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '10px 14px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        fontFamily: 'Inter, sans-serif'
      }}>
        <p style={{ margin: 0, fontWeight: 600, color: '#374151', fontSize: '13px' }}>{label}</p>
        <p style={{ margin: '4px 0 0', color: payload[0].color || '#0077cc', fontWeight: 700, fontSize: '14px' }}>
          {payload[0].name}: {unit}{payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const [username, setUsername] = useState("");
  const [currency, setCurrency] = useState("USD");

  const [data, setData] = useState({
    total_sales: 0.0,
    outstanding: 0.0,
    customers: 0,
    suppliers: 0,
  });

  const salesData = [
    { name: "Mon", sales: 200 },
    { name: "Tue", sales: 400 },
    { name: "Wed", sales: 300 },
    { name: "Thu", sales: 600 },
    { name: "Fri", sales: 500 },
  ];

  const purchaseData = [
    { name: "Mon", purchase: 150 },
    { name: "Tue", purchase: 300 },
    { name: "Wed", purchase: 200 },
    { name: "Thu", purchase: 400 },
    { name: "Fri", purchase: 350 },
  ];

  const expenseData = [
    { name: "Mon", expense: 100 },
    { name: "Tue", expense: 200 },
    { name: "Wed", expense: 150 },
    { name: "Thu", expense: 250 },
    { name: "Fri", expense: 220 },
  ];

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const storedUsername = localStorage.getItem("username");
    const savedCurrency = localStorage.getItem("currency");

    if (!token) {
      window.location.replace("/auth/login");
    } else {
      setUsername(storedUsername || "User");
      if (savedCurrency) setCurrency(savedCurrency);

      // Fetch dynamic KPI metrics from backend database
      fetch("/api/dashboard/metrics", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      })
        .then((res) => res.json())
        .then((metrics) => {
          if (metrics && typeof metrics === "object") {
            setData({
              total_sales: metrics.total_sales || 0.0,
              outstanding: metrics.outstanding || 0.0,
              customers: metrics.customers || 0,
              suppliers: metrics.suppliers || 0,
            });
          }
        })
        .catch((err) => console.error("Error loading metrics:", err));
    }
  }, []);

  const handleCurrencyChange = (e) => {
    const selectedCurrency = e.target.value;
    setCurrency(selectedCurrency);
    localStorage.setItem("currency", selectedCurrency);
  };

  const formatCurrency = (amount) => {
    return `${currency} ${amount.toFixed(2)}`;
  };

  const cardStyle = {
    background: "rgba(255,255,255,0.7)",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
    transition: "all 0.3s",
    marginBottom: "20px",
  };

  const cardHeaderStyle = {
    marginBottom: "10px",
    fontWeight: "600",
    fontSize: "1.1rem",
    color: "#004080",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const listStyle = {
    paddingLeft: "15px",
    marginTop: "10px",
  };

  return (
    <div className="dashboard-container">

      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h2>Welcome back, {username}</h2>
          <p>Business insights at a glance</p>
        </div>

        <select value={currency} onChange={handleCurrencyChange}>
          <option value="USD">USD ($)</option>
          <option value="AED">AED (د.إ)</option>
          <option value="EUR">EUR (€)</option>
        </select>
      </div>

      {/* KPI CARDS */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon sales">
            <FaMoneyBillWave />
          </div>
          <div>
            <h6>Total Sales</h6>
            <h3>{formatCurrency(data.total_sales)}</h3>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon outstanding">
            <FaWallet />
          </div>
          <div>
            <h6>Outstanding</h6>
            <h3>{formatCurrency(data.outstanding)}</h3>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon customers">
            <FaUsers />
          </div>
          <div>
            <h6>Customers</h6>
            <h3>{data.customers}</h3>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon suppliers">
            <FaTruck />
          </div>
          <div>
            <h6>Suppliers</h6>
            <h3>{data.suppliers}</h3>
          </div>
        </div>
      </div>

      {/* GRAPHS */}
      <div className="dashboard-grid">
        <ChartCard title="Sales by Region">
          <SalesByRegionChart />
        </ChartCard>

        <ChartCard title="Revenue Trend">
          <RevenueTrendChart />
        </ChartCard>

        <ChartCard title="Sales Graph">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={salesData} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" tickLine={false} axisLine={{ stroke: '#cbd5e1' }} tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
              <Tooltip content={<CustomChartTooltip unit="$" />} />
              <Area type="monotone" dataKey="sales" name="Sales" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#salesGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Purchase Graph">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={purchaseData} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" tickLine={false} axisLine={{ stroke: '#cbd5e1' }} tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
              <Tooltip content={<CustomChartTooltip unit="$" />} />
              <Line type="monotone" dataKey="purchase" name="Purchase" stroke="#3b82f6" strokeWidth={3} dot={{ r: 5, fill: "#3b82f6" }} activeDot={{ r: 7 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Expense Graph">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={expenseData} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" tickLine={false} axisLine={{ stroke: '#cbd5e1' }} tick={{ fill: '#64748b', fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
              <Tooltip content={<CustomChartTooltip unit="$" />} />
              <Bar dataKey="expense" name="Expense" fill="#f43f5e" radius={[8, 8, 0, 0]} barSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* PROFIT & LOSS + ATTENDANCE */}
      <div className="dashboard-grid">
        <div style={cardStyle}>
          <h5 style={cardHeaderStyle}><FaChartLine /> Profit & Loss Summary</h5>
          <p>Total Profit: <strong>{formatCurrency(5000)}</strong></p>
          <p>Total Loss: <strong>{formatCurrency(1200)}</strong></p>
          <p>Net Profit: <strong>{formatCurrency(3800)}</strong></p>
        </div>

        <div style={cardStyle}>
          <h5 style={cardHeaderStyle}><FaRegCalendarAlt /> Attendance Summary</h5>
          <p>Present: 20</p>
          <p>Absent: 3</p>
          <p>Leaves: 2</p>
        </div>
      </div>

      {/* DAILY SALES */}
      <div style={cardStyle}>
        <h5 style={cardHeaderStyle}><FaRegCalendarAlt /> Daily Sales Summary</h5>
        <ul style={listStyle} className="activity-list">
          <li>Monday: {formatCurrency(200)}</li>
          <li>Tuesday: {formatCurrency(400)}</li>
          <li>Wednesday: {formatCurrency(300)}</li>
        </ul>
      </div>

      {/* ACTIVITY */}
      <div style={cardStyle}>
        <h5 style={cardHeaderStyle}><FaHistory /> Recent Activity</h5>
        <ul style={listStyle} className="activity-list">
          <li><FaUsers /> New customer added</li>
          <li><FaFileInvoiceDollar /> Invoice created</li>
          <li><FaMoneyBillWave /> Payment received</li>
        </ul>
      </div>

    </div>
  );
};

export default Dashboard;
